import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  User, 
  ArrowRight,
  Clock,
  Tag,
  Sparkles
} from 'lucide-react';
import { useBlogPosts } from '../../hooks/useBlogPosts';
import { formatDate } from '../../utils/dateUtils';
import BlogPostSkeleton from '../../components/shared/BlogPostSkeleton';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const { data: postsResponse, isLoading, error } = useBlogPosts({
    limit: 20,
    category: selectedCategory !== 'All' ? selectedCategory : undefined
  });

  const blogPosts = useMemo(() => {
    if (!postsResponse?.data) return [];
    return Array.isArray(postsResponse.data) ? postsResponse.data : [];
  }, [postsResponse]);

  const categories = useMemo(() => {
    const uniqueCategories = ['All'];
    blogPosts.forEach(post => {
      if (post.category && !uniqueCategories.includes(post.category)) {
        uniqueCategories.push(post.category);
      }
    });
    return uniqueCategories;
  }, [blogPosts]);

  const featuredPost = useMemo(() => {
    return blogPosts.find(post => post.is_featured) || null;
  }, [blogPosts]);

  const regularPosts = useMemo(() => {
    return blogPosts.filter(post => !post.is_featured);
  }, [blogPosts]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white border-b border-gray-100">
        <div className="page-container py-8 sm:py-12 md:py-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-black text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-light mb-4 sm:mb-6">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Blog & Resources</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
              Event Planning Blog
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto px-2">
              Expert tips, guides, and inspiration to help you plan unforgettable events. 
              From weddings to corporate gatherings, we've got you covered.
            </p>
          </div>
        </div>
      </div>

      {/* Categories Filter */}
      <div className="py-4 sm:py-6 bg-white border-b border-gray-100 sticky top-0 z-10 backdrop-blur-sm bg-white/95">
        <div className="page-container px-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 overflow-x-auto pb-2 sm:pb-0">
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="py-12 md:py-20 bg-white">
          <div className="page-container px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <BlogPostSkeleton key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="py-12 md:py-20 bg-white">
          <div className="page-container px-4 sm:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-gray-600 mb-4">
                Failed to load blog posts. Please try again later.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Featured Post */}
      {featuredPost && !isLoading && (
        <div className="py-8 sm:py-12 md:py-20 bg-white">
          <div className="page-container px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="mb-4 sm:mb-6">
                <span className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wide">Featured Post</span>
              </div>
              <Link
                to={`/blog/${featuredPost.slug}`}
                className="block group"
              >
                <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
                  <div className="flex flex-col md:grid md:grid-cols-2 gap-0">
                    <div className="relative h-56 sm:h-64 md:h-auto md:min-h-[400px] order-2 md:order-1">
                      {featuredPost.image ? (
                        <img
                          src={featuredPost.image}
                          alt={featuredPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/800x600?text=Blog+Post';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                          <Tag className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="p-5 sm:p-6 md:p-8 lg:p-12 flex flex-col justify-center order-1 md:order-2">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                        <span className="px-2.5 sm:px-3 py-1 bg-gray-900 text-white text-xs font-medium rounded-full">
                          {featuredPost.category}
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          {featuredPost.read_time} min read
                        </span>
                      </div>
                      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 group-hover:text-gray-700 transition-colors">
                        {featuredPost.title}
                      </h2>
                      <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                        {featuredPost.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-900">{featuredPost.author_name || 'Admin'}</p>
                            {featuredPost.published_at && (
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(featuredPost.published_at, 'short')}
                              </p>
                            )}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-gray-900 group-hover:translate-x-1 transition-all flex-shrink-0" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Blog Posts Grid */}
      {!isLoading && regularPosts.length > 0 && (
        <div className="py-8 sm:py-12 md:py-20 bg-gray-50">
          <div className="page-container px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {regularPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="block group"
                  >
                    <div className="bg-white rounded-lg sm:rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                      <div className="relative h-40 sm:h-48">
                        {post.image ? (
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/800x600?text=Blog+Post';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                            <Tag className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
                          </div>
                        )}
                        {post.category && (
                          <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                            <span className="px-2 sm:px-3 py-1 bg-white text-gray-900 text-xs font-medium rounded-full">
                              {post.category}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-4 sm:p-6 flex-1 flex flex-col">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-3 text-xs text-gray-500">
                          {post.published_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(post.published_at, 'short')}
                            </span>
                          )}
                          {post.read_time && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.read_time} min read
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3 group-hover:text-gray-700 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed flex-1 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                              <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" />
                            </div>
                            <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">{post.author_name || 'Admin'}</span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-900 group-hover:translate-x-1 transition-all flex-shrink-0" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && regularPosts.length === 0 && !featuredPost && (
        <div className="py-12 md:py-20 bg-gray-50">
          <div className="page-container px-4 sm:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-gray-600 mb-4">
                No blog posts found{selectedCategory !== 'All' ? ` in ${selectedCategory}` : ''}.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Section */}
      <div className="py-8 sm:py-12 md:py-20 bg-white">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-white text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
                Stay Updated
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto">
                Subscribe to our newsletter and get the latest event planning tips, guides, and inspiration delivered to your inbox.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-gray-900 border-0 focus:ring-2 focus:ring-white text-sm sm:text-base"
                />
                <button className="px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-black rounded-lg sm:rounded-xl font-light hover:bg-gray-100 transition-colors whitespace-nowrap text-sm sm:text-base">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;

